import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Header } from "./../Layouts/Header";
import { get_floors, get_all_orders, getDeliverOrder, refresh_time } from "./apis";
import { data } from "../variables/variables";
import { Modal } from "react-bootstrap";
import DeliveryRooms from "./DeliveryRooms";
import { useOrderRealtime } from "../Utils/useOrderRealtime";
import "./../Assets/css/main.css";
import "./../Assets/css/utility.css";

/** Reception + kitchen — never call get-order without a department id. */
const DELIVERY_DEPARTMENT_IDS = [2, 3];

const POLL_MS = () => {
  const ms = Number(refresh_time);
  const base = Number.isFinite(ms) && ms >= 1000 ? ms : 5000;
  return Math.max(base, 8000);
};

const isDispatchedOrder = (order) => {
  const statusName = String(order?.status?.name || "")
    .toLowerCase()
    .trim();
  if (statusName === "dispatched") return true;
  const statusId = Number(order?.status_id ?? order?.status?.id);
  return statusId === 3;
};

const orderRoomId = (order) => {
  const id = order?.room_id ?? order?.room?.id;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const applyDispatchHighlightToFloors = (floors, dispatchedRoomIds) => {
  return floors.map((floor) => {
    const rooms = (floor?.rooms || []).map((room) => {
      const roomId = Number(room.id);
      const hasDispatch = dispatchedRoomIds.has(roomId);
      return {
        ...room,
        ord_status: hasDispatch ? 3 : 0,
        has_dispatch: hasDispatch,
      };
    });
    const floorHasDispatch = rooms.some((r) => r.has_dispatch);
    return {
      ...floor,
      rooms,
      has_dispatch: floorHasDispatch,
      ord_status: floorHasDispatch ? 3 : 0,
    };
  });
};

const extractDispatchedRoomIds = (orders) => {
  const ids = new Set();
  (orders || []).filter(isDispatchedOrder).forEach((order) => {
    const roomId = orderRoomId(order);
    if (roomId) ids.add(roomId);
  });
  return ids;
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function DeliveryModule() {
  const [floors, setfloors] = useState([]);
  const [redirectLogin, setRedirectLogin] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [responseApi, setResponseApi] = useState(0);
  const [room_Modal, setRoom_Modal] = useState(false);
  const [floorRooms, setFloorRooms] = useState([]);
  const [floorName, setFloorName] = useState("");
  const baseFloorsRef = useRef([]);
  const tokenRef = useRef(null);
  const pollPausedUntilRef = useRef(0);
  const pollInFlightRef = useRef(false);
  const openFloorRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user"));
  tokenRef.current = user?.token;

  const authHeaders = () => ({
    Authorization: "Bearer " + tokenRef.current,
  });

  /** 2 requests: get-order/2 and get-order/3 only (safe for throttle). */
  const fetchFromDepartments = async () => {
    const ids = new Set();
    const results = await Promise.all(
      DELIVERY_DEPARTMENT_IDS.map((deptId) =>
        axios
          .get(`${get_all_orders}/${deptId}`, { headers: authHeaders() })
          .then((res) => res)
          .catch((err) => {
            if (err?.response?.status === 429) throw err;
            return null;
          })
      )
    );
    results.forEach((res) => {
      if (res?.data?.status === 200) {
        extractDispatchedRoomIds(res?.data?.orders).forEach((id) => ids.add(id));
      }
    });
    return ids;
  };

  /** Same API as room modal — one room at a time to avoid 429. */
  const fetchFromDeliverEndpoint = async (rooms) => {
    const ids = new Set();
    for (const room of rooms || []) {
      const roomId = Number(room?.id);
      if (!roomId) continue;
      try {
        const res = await axios.get(getDeliverOrder + roomId, {
          headers: authHeaders(),
        });
        if (res?.data?.status === 200) {
          const raw = res?.data?.dispatched;
          const list = Array.isArray(raw) ? raw : raw != null ? [raw] : [];
          if (list.length > 0) ids.add(roomId);
        }
      } catch (err) {
        if (err?.response?.status === 429) throw err;
      }
      await delay(120);
    }
    return ids;
  };

  const fetchDispatchedRoomIds = async (openFloorRooms = null) => {
    const ids = await fetchFromDepartments();

    if (openFloorRooms?.length) {
      const deliverIds = await fetchFromDeliverEndpoint(openFloorRooms);
      deliverIds.forEach((id) => ids.add(id));
    }

    return ids;
  };

  const applyHighlights = (dispatchedRoomIds) => {
    const updated = applyDispatchHighlightToFloors(
      JSON.parse(JSON.stringify(baseFloorsRef.current)),
      dispatchedRoomIds
    );
    setfloors(updated);

    if (openFloorRef.current) {
      const floor = updated.find(
        (f) => f.floor_name === openFloorRef.current.floor_name
      );
      if (floor?.rooms) {
        setFloorRooms(floor.rooms);
      }
    }
  };

  const refreshDispatchHighlights = async (openFloorRooms = null) => {
    if (!baseFloorsRef.current?.length) return;
    if (Date.now() < pollPausedUntilRef.current) return;
    if (pollInFlightRef.current) return;

    pollInFlightRef.current = true;

    try {
      const dispatchedRoomIds = await fetchDispatchedRoomIds(openFloorRooms);
      applyHighlights(dispatchedRoomIds);
    } catch (err) {
      if (err?.response?.status === 429) {
        pollPausedUntilRef.current = Date.now() + 30000;
      }
      if (
        err?.response?.status === 401 ||
        err?.response?.status === 403
      ) {
        setRedirectLogin(true);
      }
    } finally {
      pollInFlightRef.current = false;
    }
  };

  useEffect(() => {
    axios
      .get(get_floors, { headers: authHeaders() })
      .then((res) => {
        if (res?.data?.status === 200) {
          const initial = res?.data?.floors || [];
          baseFloorsRef.current = JSON.parse(JSON.stringify(initial));
          setfloors(initial);
          refreshDispatchHighlights();
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setRedirectLogin(true);
        } else if (err?.response?.status === 429) {
          pollPausedUntilRef.current = Date.now() + 30000;
        } else {
          setFetchError(data["error"]);
          setResponseApi(1);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useOrderRealtime(
    () => {
      refreshDispatchHighlights(null);
    },
    { fallbackMs: POLL_MS() }
  );

  const CancelDeliverdModal = () => {
    setRoom_Modal(false);
    openFloorRef.current = null;
  };

  const handlefloorEvent = (item) => {
    openFloorRef.current = item;
    setFloorName(item?.floor_name);
    setFloorRooms(item?.rooms);
    setRoom_Modal(true);
    refreshDispatchHighlights(item?.rooms);
  };

  return (
    <>
      <div className="wrapper">
        <Header userName="Delivery" />
        <div className="container mt-5">
          <h4 className="text-center">DELIVERY MODULE</h4>
          <div className="floor_layout_set mt-5 d-flex justify-content-center">
            {floors.map((item) => (
              <div
                key={item?.id ?? item?.floor_name}
                onClick={() => handlefloorEvent(item)}
                className={`floordesign cursor-pointer ${item?.has_dispatch ? "delivery-dispatch-floor" : ""}`}
                style={{
                  backgroundColor: item?.has_dispatch ? "red" : "lightblue",
                  color: item?.has_dispatch ? "#fff" : undefined,
                }}
              >
                {item.floor_name}
              </div>
            ))}
          </div>
        </div>
      </div>
      <Modal
        show={room_Modal}
        onHide={CancelDeliverdModal}
        className="layout_set"
      >
        <Modal.Header style={{ backgroundColor: "lightblue" }}>
          <Modal.Title>{`${floorName} Rooms`}</Modal.Title>
          <p className="text-bold cursor-pointer" onClick={CancelDeliverdModal}>
            x
          </p>
        </Modal.Header>
        <Modal.Body>
          <DeliveryRooms
            floorRooms={floorRooms}
            onRoomModalClose={() =>
              refreshDispatchHighlights(openFloorRef.current?.rooms ?? null)
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <div className="custom_flex justify-content-end">
            <button
              onClick={CancelDeliverdModal}
              className="btn btn-secondary btn-sm active"
              role="button"
            >
              Cancel
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default DeliveryModule;
